import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { STAGES, Stage, Stage as StageType, STAGE_LABELS } from '@/types';
import { ANALYSIS_THRESHOLD } from '@/data/mockData';
import AIAnalysisPanel from '@/components/pitch/AIAnalysisPanel';
import AssignmentPanel from '@/components/pitch/AssignmentPanel';
import MeetingPanel from '@/components/pitch/MeetingPanel';
import DueDiligencePanel from '@/components/pitch/DueDiligencePanel';
import ContractPanel from '@/components/pitch/ContractPanel';
import BootcampPanel from '@/components/pitch/BootcampPanel';
import FundingPanel from '@/components/pitch/FundingPanel';
import ProjectionsPanel from '@/components/pitch/ProjectionsPanel';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/components/ui/use-toast';
import { 
  Building2, 
  Target, 
  Users, 
  DollarSign, 
  Globe, 
  Cpu, 
  AlertOctagon,
  TrendingUp,
  Briefcase,
  Shield,
  CheckCircle,
  XCircle,
  Rocket,
  Brain,
  ArrowLeft,
  Lock,
  Clock,
  Calendar,
  FileSignature,
  GraduationCap,
  Search,
  Eye,
  ChevronRight,
  Loader2,
  Wifi,
  WifiOff
} from 'lucide-react';

type TabKey = 'overview' | 'analysis' | 'assignment' | 'meeting' | 'contract' | 'bootcamp' | 'diligence' | 'funding' | 'projections';

// Define stage order
const STAGE_ORDER: Stage[] = [
  'idea',
  'analysis',
  'awaiting_assignment',
  'validation',
  'contract',
  'bootcamp',
  'diligence',
  'funding'
];

// Map stages to their display names
const STAGE_DISPLAY_NAMES: Record<Stage, string> = {
  'idea': 'Idea',
  'analysis': 'Analysis',
  'awaiting_assignment': 'Awaiting',
  'validation': 'Meeting',
  'contract': 'Contract',
  'bootcamp': 'Bootcamp',
  'diligence': 'Diligence',
  'funding': 'Funding'
};

// Map stages to their tabs
const STAGE_TO_TAB: Record<Stage, TabKey> = {
  'idea': 'overview',
  'analysis': 'analysis',
  'awaiting_assignment': 'assignment',
  'validation': 'meeting',
  'contract': 'contract',
  'bootcamp': 'bootcamp',
  'diligence': 'diligence',
  'funding': 'funding'
};

// Define which tabs are accessible at each stage
const ACCESSIBLE_TABS: Record<Stage, TabKey[]> = {
  'idea': ['overview'],
  'analysis': ['overview', 'analysis'],
  'awaiting_assignment': ['overview', 'analysis', 'assignment'],
  'validation': ['overview', 'analysis', 'assignment', 'meeting'],
  'contract': ['overview', 'analysis', 'assignment', 'meeting', 'contract'],
  'bootcamp': ['overview', 'analysis', 'assignment', 'meeting', 'contract', 'bootcamp'],
  'diligence': ['overview', 'analysis', 'assignment', 'meeting', 'contract', 'bootcamp', 'diligence'],
  'funding': ['overview', 'analysis', 'assignment', 'meeting', 'contract', 'bootcamp', 'diligence', 'funding', 'projections']
};

export default function PitchDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { pitches, submitForAnalysis, clearNotificationsForPitch, refreshPitches, isSupabaseConnected } = useData();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [analyzing, setAnalyzing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const pitch = pitches.find(p => p.id === id);
  const currentTab = (searchParams.get('tab') as TabKey) || 'overview';

  useEffect(() => {
    const loadPitch = async () => {
      setIsLoading(true);
      await refreshPitches();
      setIsLoading(false);
    };
    loadPitch();
  }, [id, refreshPitches]);

  useEffect(() => {
    if (id && currentTab) {
      clearNotificationsForPitch(id, currentTab);
    }
  }, [id, currentTab, clearNotificationsForPitch]);

  const handleAnalyze = async () => {
    if (!pitch) return;
    setAnalyzing(true);
    try {
      await submitForAnalysis(pitch.id);
      setSearchParams({ tab: 'analysis' });
      toast({
        title: "Analysis Complete",
        description: "Your pitch has been analyzed. Check the results in the Analysis tab.",
      });
    } catch (error) {
      console.error('Analysis failed:', error);
      toast({
        title: "Analysis Failed",
        description: "There was an error analyzing your pitch. Please try again.",
        variant: "destructive",
      });
    } finally {
      setAnalyzing(false);
    }
  };

  // Handle back button navigation based on user role
  const handleBack = () => {
    if (user?.role === 'senior_analyst') {
      navigate('/senior-analyst/pitches');
    } else if (user?.role === 'analyst') {
      navigate('/analyst/pitches');
    } else if (user?.role === 'founder') {
      navigate('/pitches');
    } else {
      navigate(-1);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-muted-foreground" />
          <p className="text-muted-foreground">Loading pitch...</p>
        </div>
      </div>
    );
  }

  if (!pitch) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Pitch not found.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
      </div>
    );
  }

  if (pitch.deletedAt) {
    return (
      <div className="text-center py-12">
        <Card className="max-w-md mx-auto border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
            <p className="text-lg font-medium text-red-700">Pitch Deleted</p>
            <p className="text-sm text-red-600 mt-1">This pitch is no longer accessible.</p>
            <Button variant="outline" className="mt-4" onClick={() => navigate('/dashboard')}>
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Calculate stage progress
  const currentStageIndex = STAGE_ORDER.indexOf(pitch.stage);
  const totalStages = STAGE_ORDER.length;
  const progressPercentage = ((currentStageIndex + 1) / totalStages) * 100;

  // Check if a tab is accessible
  const isTabAccessible = (tab: TabKey): boolean => {
    const accessibleTabs = ACCESSIBLE_TABS[pitch.stage] || ['overview'];
    return accessibleTabs.includes(tab);
  };

  // Get stage status for display
  const getStageStatus = (stage: Stage) => {
    const stageIndex = STAGE_ORDER.indexOf(stage);
    if (stageIndex < currentStageIndex) return 'completed';
    if (stageIndex === currentStageIndex) return 'current';
    return 'upcoming';
  };

  // Handle tab change with access control
  const handleTabChange = (tab: TabKey) => {
    if (isTabAccessible(tab)) {
      setSearchParams({ tab });
    } else {
      toast({
        title: "Stage locked",
        description: "You need to complete the current stage first.",
        variant: "destructive"
      });
    }
  };

  // Handle stage click from roadmap
  const handleStageClick = (stage: Stage) => {
    const stageIndex = STAGE_ORDER.indexOf(stage);
    const currentIndex = STAGE_ORDER.indexOf(pitch.stage);
    
    // Can only click on completed or current stages
    if (stageIndex <= currentIndex) {
      setSearchParams({ tab: STAGE_TO_TAB[stage] });
    } else {
      toast({
        title: "Stage locked",
        description: "Complete the current stage first.",
        variant: "destructive"
      });
    }
  };

  // Render content with access control
  const renderContent = () => {
    // If tab is not accessible, show locked message
    if (!isTabAccessible(currentTab)) {
      return (
        <Card className="bg-gray-50">
          <CardContent className="py-12 text-center">
            <Lock className="h-16 w-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-semibold mb-2">Stage Locked</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              You need to complete the {STAGE_DISPLAY_NAMES[pitch.stage]} stage before you can access this content.
            </p>
          </CardContent>
        </Card>
      );
    }

    // Render accessible tab content
    switch (currentTab) {
      case 'overview':
        return (
          <ComprehensivePitchOverview 
            pitch={pitch} 
            onAnalyze={handleAnalyze}
            analyzing={analyzing}
            showAnalyzeButton={!pitch.analysisScores && pitch.stage === 'idea'}
          />
        );
      case 'analysis':
        return pitch.analysisScores ? (
          <AIAnalysisPanel
            scores={pitch.analysisScores}
            passed={pitch.analysisScores.overall >= ANALYSIS_THRESHOLD}
          />
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Brain className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-lg font-medium">No Analysis Yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Submit your pitch for AI analysis to see scores and feedback.
              </p>
              <Button 
                onClick={handleAnalyze} 
                disabled={analyzing}
                className="mt-4"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  'Submit for Analysis'
                )}
              </Button>
            </CardContent>
          </Card>
        );
      case 'assignment':
        return <AssignmentPanel pitch={pitch} />;
      case 'meeting':
        return <MeetingPanel pitch={pitch} />;
      case 'contract':
        return <ContractPanel pitch={pitch} />;
      case 'bootcamp':
        return <BootcampPanel pitch={pitch} />;
      case 'diligence':
        return <DueDiligencePanel pitch={pitch} />;
      case 'funding':
        return <FundingPanel pitch={pitch} />;
      case 'projections':
        return <ProjectionsPanel pitch={pitch} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-4 w-full">
      {/* Back button and connection status */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={handleBack} className="gap-2 h-8 px-2">
          <ArrowLeft className="h-3 w-3" />
          Back
        </Button>
        <div className="flex items-center gap-2">
          {isSupabaseConnected ? (
            <div className="flex items-center gap-1">
              <Wifi className="h-3 w-3 text-green-500" />
              <span className="text-xs text-green-600">Live</span>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <WifiOff className="h-3 w-3 text-red-500" />
              <span className="text-xs text-red-600">Offline</span>
            </div>
          )}
        </div>
      </div>

      {/* Header with company name and stage badge */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold">{pitch.companyName}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{pitch.tagline}</p>
        </div>
        <Badge className="text-xs px-3 py-1 bg-blue-100 text-blue-800 border-blue-200">
          {STAGE_LABELS[pitch.stage]}
        </Badge>
      </div>

      {/* SINGLE UNIFIED ROADMAP - Compact version */}
      <Card className="overflow-hidden border-l-4 border-l-blue-500">
        <CardContent className="p-3">
          {/* Progress info - compact */}
          <div className="flex items-center justify-between mb-2 text-xs">
            <div className="flex items-center gap-2">
              <span className="font-medium">Progress:</span>
              <span className="text-muted-foreground">
                Stage {currentStageIndex + 1}/{totalStages}
              </span>
            </div>
            <span className="text-muted-foreground">
              {Math.round(progressPercentage)}%
            </span>
          </div>

          {/* Progress bar - thinner */}
          <Progress value={progressPercentage} className="h-1.5 mb-3" />

          {/* Interactive stage pills - Horizontal scroll on mobile, grid on desktop */}
          <div className="flex md:grid md:grid-cols-8 gap-1 overflow-x-auto pb-2 md:pb-0">
            {STAGE_ORDER.map((stage, index) => {
              const status = getStageStatus(stage);
              const isCompleted = status === 'completed';
              const isCurrent = status === 'current';
              const isUpcoming = status === 'upcoming';
              
              return (
                <Button
                  key={stage}
                  variant="ghost"
                  size="sm"
                  className={`flex-shrink-0 h-auto py-1.5 px-2 flex flex-col items-center gap-0.5 min-w-[70px] md:min-w-0 ${
                    isCompleted ? 'text-green-600 hover:text-green-700 hover:bg-green-50' :
                    isCurrent ? 'text-blue-600 font-medium hover:text-blue-700 hover:bg-blue-50' :
                    'text-gray-400 hover:text-gray-500'
                  }`}
                  onClick={() => handleStageClick(stage)}
                  disabled={isUpcoming}
                >
                  <div className="flex items-center gap-0.5">
                    {isCompleted && <CheckCircle className="h-2.5 w-2.5" />}
                    {isCurrent && <Clock className="h-2.5 w-2.5" />}
                    {isUpcoming && <Lock className="h-2.5 w-2.5" />}
                    <span className="text-[10px] font-medium">
                      {index + 1}
                    </span>
                  </div>
                  <span className={`text-[10px] font-medium ${
                    isCompleted ? 'text-green-600' :
                    isCurrent ? 'text-blue-600' :
                    'text-gray-400'
                  }`}>
                    {STAGE_DISPLAY_NAMES[stage]}
                  </span>
                </Button>
              );
            })}
          </div>

          {/* Current stage info - compact */}
          <div className="mt-2 pt-2 border-t text-xs">
            <div className="flex items-start gap-2">
              <div className={`p-1 rounded-full ${
                getStageStatus(pitch.stage) === 'completed' ? 'bg-green-100' :
                getStageStatus(pitch.stage) === 'current' ? 'bg-blue-100' :
                'bg-gray-100'
              }`}>
                {getStageStatus(pitch.stage) === 'completed' && <CheckCircle className="h-3 w-3 text-green-600" />}
                {getStageStatus(pitch.stage) === 'current' && <Clock className="h-3 w-3 text-blue-600" />}
              </div>
              <div>
                <span className={`font-medium ${
                  getStageStatus(pitch.stage) === 'completed' ? 'text-green-700' :
                  getStageStatus(pitch.stage) === 'current' ? 'text-blue-700' :
                  'text-gray-700'
                }`}>
                  {STAGE_DISPLAY_NAMES[pitch.stage]}
                </span>
                <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1">
                  {pitch.stage === 'idea' && "Submit for AI analysis"}
                  {pitch.stage === 'analysis' && "Analysis in progress"}
                  {pitch.stage === 'awaiting_assignment' && "Waiting for analyst"}
                  {pitch.stage === 'validation' && "Schedule meeting"}
                  {pitch.stage === 'contract' && "Sign agreement"}
                  {pitch.stage === 'bootcamp' && "Complete tasks"}
                  {pitch.stage === 'diligence' && "Verify information"}
                  {pitch.stage === 'funding' && "Milestone tracking"}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 border-b pb-2">
        {(['overview', 'analysis', 'assignment', 'meeting', 'contract', 'bootcamp', 'diligence', 'funding', 'projections'] as TabKey[]).map((tab) => {
          const isAccessible = isTabAccessible(tab);
          return (
            <Button
              key={tab}
              variant={currentTab === tab ? "default" : "ghost"}
              size="sm"
              onClick={() => handleTabChange(tab)}
              disabled={!isAccessible}
              className="capitalize"
            >
              {tab}
              {!isAccessible && <Lock className="h-3 w-3 ml-1" />}
            </Button>
          );
        })}
      </div>

      {/* Content area */}
      <div className="w-full">
        {renderContent()}
      </div>
    </div>
  );
}

// ===== COMPREHENSIVE PITCH OVERVIEW =====
function ComprehensivePitchOverview({ 
  pitch, 
  onAnalyze, 
  analyzing,
  showAnalyzeButton 
}: { 
  pitch: any; 
  onAnalyze: () => void;
  analyzing: boolean;
  showAnalyzeButton: boolean;
}) {
  
  const sections = [
    {
      title: 'Company Overview',
      icon: Building2,
      color: 'blue',
      items: [
        { label: 'Company Name', value: pitch.companyName },
        { label: 'Tagline', value: pitch.tagline },
        { label: 'Problem', value: pitch.problem, long: true },
        { label: 'Solution', value: pitch.solution, long: true },
        { label: 'Market Size', value: pitch.marketSize, long: true },
        { label: 'Business Model', value: pitch.businessModel, long: true },
        { label: 'Team', value: pitch.team, long: true },
      ]
    },
    {
      title: 'Traction & Validation',
      icon: TrendingUp,
      color: 'green',
      items: [
        { label: 'Customer Interviews', value: pitch.traction?.customerInterviews },
        { label: 'Waitlist Signups', value: pitch.traction?.waitlistSignups },
        { label: 'Letters of Intent', value: pitch.traction?.lettersOfIntent },
        { label: 'Pilot Customers', value: pitch.traction?.pilotCustomers },
        { label: 'MVP Status', value: pitch.traction?.mvpStatus },
        { label: 'MVP URL', value: pitch.traction?.mvpUrl },
        { label: 'Monthly Active Users', value: pitch.traction?.monthlyActiveUsers },
        { label: 'Monthly Revenue', value: pitch.traction?.revenueRunRate ? `$${pitch.traction.revenueRunRate.toLocaleString()}` : null },
      ]
    },
    {
      title: 'Competitive Landscape',
      icon: Target,
      color: 'purple',
      items: [
        { label: 'Direct Competitors', value: pitch.competitive?.directCompetitors?.join(', ') },
        { label: 'Indirect Competitors', value: pitch.competitive?.indirectCompetitors?.join(', ') },
        { label: 'Competitive Advantage', value: pitch.competitive?.competitiveAdvantage, long: true },
        { label: 'Barriers to Entry', value: pitch.competitive?.barriersToEntry?.join(', ') },
        { label: 'Substitute Products', value: pitch.competitive?.substituteProducts?.join(', ') },
      ]
    },
    {
      title: 'Financial Projections',
      icon: DollarSign,
      color: 'green',
      items: [
        { label: 'Funding Ask', value: pitch.financial?.fundingAsk ? `$${pitch.financial.fundingAsk.toLocaleString()}` : null },
        { label: 'Runway', value: pitch.financial?.runwayMonths ? `${pitch.financial.runwayMonths} months` : null },
        { label: 'Burn Rate', value: pitch.financial?.burnRate ? `$${pitch.financial.burnRate.toLocaleString()}/month` : null },
        { label: 'Breakeven Month', value: pitch.financial?.breakevenMonth ? `Month ${pitch.financial.breakevenMonth}` : null },
        { label: 'Revenue Projections', value: pitch.financial?.revenueProjection ? 
          `M1: $${pitch.financial.revenueProjection.month1?.toLocaleString()}, M3: $${pitch.financial.revenueProjection.month3?.toLocaleString()}, M6: $${pitch.financial.revenueProjection.month6?.toLocaleString()}, M12: $${pitch.financial.revenueProjection.month12?.toLocaleString()}` 
          : null 
        },
      ]
    },
    {
      title: 'Go-to-Market Strategy',
      icon: Globe,
      color: 'orange',
      items: [
        { label: 'Customer Channels', value: pitch.gtm?.customerChannels?.join(', ') },
        { label: 'Sales Strategy', value: pitch.gtm?.salesStrategy, long: true },
        { label: 'Marketing Plan', value: pitch.gtm?.marketingPlan, long: true },
        { label: 'Partnerships', value: pitch.gtm?.partnerships?.join(', ') },
        { label: 'Target Early Adopters', value: pitch.gtm?.targetEarlyAdopters, long: true },
      ]
    },
    {
      title: 'IP & Technology',
      icon: Cpu,
      color: 'indigo',
      items: [
        { label: 'Patent Status', value: pitch.ip?.patentStatus },
        { label: 'Patent Numbers', value: pitch.ip?.patentNumbers?.join(', ') },
        { label: 'Proprietary Technology', value: pitch.ip?.proprietaryTechnology, long: true },
        { label: 'Technical Architecture', value: pitch.ip?.technicalArchitecture, long: true },
        { label: 'Development Stage', value: pitch.ip?.developmentStage },
        { label: 'Tech Stack', value: pitch.ip?.techStack?.join(', ') },
      ]
    },
    {
      title: 'Risks & Mitigations',
      icon: AlertOctagon,
      color: 'red',
      items: [
        { label: 'Regulatory Hurdles', value: pitch.risks?.regulatoryHurdles?.join(', ') },
        { label: 'Compliance Requirements', value: pitch.risks?.complianceRequirements?.join(', ') },
        { label: 'Market Risks', value: pitch.risks?.marketRisks?.join(', ') },
        { label: 'Execution Risks', value: pitch.risks?.executionRisks?.join(', ') },
        { 
          label: 'Key Risks', 
          value: pitch.risks?.keyRisks?.map((r: any) => 
            `${r.risk} (${r.impact} impact) - Mitigation: ${r.mitigation}`
          ).join(' | ') 
        },
      ]
    },
  ];

  const getIconColor = (color: string) => {
    const colors = {
      blue: 'text-blue-600 bg-blue-100',
      green: 'text-green-600 bg-green-100',
      purple: 'text-purple-600 bg-purple-100',
      orange: 'text-orange-600 bg-orange-100',
      indigo: 'text-indigo-600 bg-indigo-100',
      red: 'text-red-600 bg-red-100',
    };
    return colors[color as keyof typeof colors] || 'text-gray-600 bg-gray-100';
  };

  return (
    <div className="space-y-4">
      {sections.map((section) => {
        const Icon = section.icon;
        const hasItems = section.items.some(item => 
          item.value && 
          item.value !== 0 && 
          item.value !== '0' && 
          item.value !== 'none' &&
          item.value !== ''
        );
        
        if (!hasItems) return null;
        
        return (
          <Card key={section.title} className="overflow-hidden border-l-4 border-l-blue-500">
            <CardHeader className="bg-muted/30 py-2">
              <div className="flex items-center gap-2">
                <div className={`p-1 rounded-lg ${getIconColor(section.color)}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <CardTitle className="text-base">{section.title}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-3">
              <div className="space-y-2">
                {section.items.map((item, idx) => {
                  if (!item.value || item.value === 0 || item.value === '0' || item.value === 'none' || item.value === '') return null;
                  
                  return (
                    <div key={idx}>
                      <div className="grid grid-cols-12 gap-2 text-sm">
                        <div className="col-span-3 text-xs font-medium text-muted-foreground">
                          {item.label}
                        </div>
                        <div className="col-span-9">
                          {item.long ? (
                            <div className="text-xs bg-muted/20 p-2 rounded border">
                              {item.value}
                            </div>
                          ) : (
                            <p className="text-xs">{item.value}</p>
                          )}
                        </div>
                      </div>
                      {idx < section.items.length - 1 && <Separator className="my-1" />}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* AI Analysis Button */}
      {showAnalyzeButton && (
        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardContent className="pt-4">
            <div className="flex flex-col items-center text-center py-2">
              <Rocket className="h-8 w-8 text-blue-600 mb-2" />
              <h3 className="text-base font-semibold mb-1">Ready for AI Analysis?</h3>
              <p className="text-xs text-muted-foreground mb-3 max-w-lg">
                Get detailed scores and feedback across all categories.
              </p>
              <Button 
                onClick={onAnalyze} 
                disabled={analyzing}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  'Submit for Analysis'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Already Analyzed Message */}
      {pitch.analysisScores && (
        <Alert className="bg-green-50 border-green-200 py-2">
          <CheckCircle className="h-3 w-3 text-green-600" />
          <AlertDescription className="text-xs text-green-700">
            ✓ This pitch has been analyzed. View results in Analysis tab.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}